import { AuthenticationManager } from '@frontend/authentication-v2';
import { useAccountRepository } from '@frontend/repository';
import { MySettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { ToastUtil } from '@frontend/toast-utils';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

export type BackupNotificationLoadStatus = 'loading' | 'ready' | 'error';

const useBackupNotification = () => {
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [initialUser, setInitialUser] = useState<string | null>(null);
    const { currentAccount } = useAccountRepository();
    const [tokenUser, setTokenUser] = useState<IUser | null>(null);
    const [loadStatus, setLoadStatus] = useState<BackupNotificationLoadStatus>('loading');
    const [loadAttempt, setLoadAttempt] = useState(0);

    const getCurrentUserId = async (): Promise<string | null> => {
        const token = await AuthenticationManager.getInstance().getTokenWhenReady();
        return token ? token.entity_id : null;
    };

    const findBackupSetting = async () => {
        const res = await MySettingClient.fetchMySettings({ key: SettingKey.BACKUP_NOTIFICATION_USER_ID });
        return res.results[0] || null;
    };

    const retry = () => {
        setLoadAttempt((n) => n + 1);
    };

    useEffect(() => {
        if (!currentAccount) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoadStatus('loading');
            setTokenUser(null);

            try {
                const currentUser = await getCurrentUserId();
                if (!currentUser) {
                    if (!cancelled) setLoadStatus('error');
                    return;
                }

                const res = await UserClient.resolveUsers([currentUser]);
                if (cancelled) return;

                if (res.results.length === 0) {
                    setLoadStatus('error');
                    return;
                }

                setTokenUser(res.results[0]);
                setLoadStatus('ready');

                try {
                    const found = await findBackupSetting();
                    if (cancelled || !found) return;
                    const value = found.value || '';
                    setInitialUser(typeof value === 'string' ? value : String(value));
                } catch {
                    // Setting is optional for showing the form
                }
            } catch {
                if (!cancelled) setLoadStatus('error');
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [currentAccount, loadAttempt]);

    const confirm = async (userId: string | null) => {
        if (!userId || saving || success) return;
        if (!currentAccount) {
            return;
        }

        setSaving(true);
        try {
            const currentUser = await getCurrentUserId();
            if (!currentUser) throw new Error('No token');

            const found = await findBackupSetting();
            if (found) {
                await MySettingClient.patchMySetting(found.id, { value: userId });
            } else {
                await MySettingClient.postMySetting({
                    key: SettingKey.BACKUP_NOTIFICATION_USER_ID,
                    value: userId
                });
            }

            setSuccess(true);
            setInitialUser(userId);
            ToastUtil.success(
                <FormattedMessage
                    id='change-backup-notification-user.success-message'
                    description='Success message shown after updating the backup notification user'
                    defaultMessage='Successfully updated backup notification user.'
                />
            );
        } catch (err) {
            ToastUtil.error(
                <FormattedMessage
                    id='change-backup-notification-user.failure-message'
                    description='Error toast when updating the backup notification user fails'
                    defaultMessage='Failed to update backup notification user. Please try again.'
                />
            );
        } finally {
            setSaving(false);
        }
    };

    return {
        confirm,
        saving,
        success,
        initialUser,
        tokenUser,
        loadStatus,
        retry
    };
};

export default useBackupNotification;
